import { ApiVersions } from '@gonfalon/openapi/api-versions';
import { getClient } from '@gonfalon/openapi/client';
import { createIntegrationConfiguration } from '@gonfalon/openapi/operations/createIntegrationConfiguration';
import { deleteIntegrationConfiguration } from '@gonfalon/openapi/operations/deleteIntegrationConfiguration';
import { getAllIntegrationConfigurations } from '@gonfalon/openapi/operations/getAllIntegrationConfigurations';
import { getIntegrationConfiguration } from '@gonfalon/openapi/operations/getIntegrationConfiguration';
import { updateIntegrationConfiguration } from '@gonfalon/openapi/operations/updateIntegrationConfiguration';
import type { schemas } from '@gonfalon/openapi/types';
import { getQueryClient } from '@gonfalon/react-query-client/getQueryClient';
import { useMutation } from '@tanstack/react-query';
import type { FetchResponse } from 'openapi-fetch';
import type { MediaType } from 'openapi-typescript-helpers';

import { createMutationHook } from '../internal/createMutationHook';
import { createQueryOptions } from '../internal/createQueryOptions';
import { reactQueryResponseAdapter } from '../internal/reactQueryResponseAdapter';

export const integrationConfigurationQuery = createQueryOptions(getIntegrationConfiguration);
export const integrationConfigurationsQuery = createQueryOptions(getAllIntegrationConfigurations);

export const useCreateIntegrationConfiguration = createMutationHook(createIntegrationConfiguration, {
  onSuccess: async (data) => {
    const queryClient = getQueryClient();
    // Seed the detail query with the create response before navigating to the
    // edit page. The response contains server-assigned fields such as
    // _maintainerId; relying only on a list invalidation can briefly render a
    // stale config without its maintainer until the page is refreshed.
    queryClient.setQueryData(integrationConfigurationQuery({ integrationConfigurationId: data._id }).queryKey, data);
    await queryClient.invalidateQueries({ queryKey: integrationConfigurationsQuery.partialQueryKey() });
  },
});

export const useUpdateIntegrationConfiguration = createMutationHook(updateIntegrationConfiguration, {
  onSuccess: async (data, { integrationConfigurationId }) => {
    const queryClient = getQueryClient();
    // The PATCH response is the same representation GET serves, so make it the detail
    // query's data right away. Consumers that show the saved value (maintainer, refresh
    // frequency) are then authoritative even if the follow-up refetch is slow or fails,
    // and an in-flight stale refetch is cancelled by the invalidation below.
    queryClient.setQueryData(integrationConfigurationQuery({ integrationConfigurationId }).queryKey, data);
    await queryClient.invalidateQueries({
      queryKey: integrationConfigurationQuery.partialQueryKey({ integrationConfigurationId }),
    });
    await queryClient.invalidateQueries({ queryKey: integrationConfigurationsQuery.partialQueryKey() });
  },
});

export const useDeleteIntegrationConfiguration = createMutationHook(deleteIntegrationConfiguration, {
  onSuccess: async (data, { integrationConfigurationId }) => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({
      queryKey: integrationConfigurationQuery.partialQueryKey({ integrationConfigurationId }),
    });
    await queryClient.invalidateQueries({ queryKey: integrationConfigurationsQuery.partialQueryKey() });
  },
});

type IntegrationConfigurationResponse = schemas['IntegrationConfigurationsRep'];
// Two-step warehouse setup (Snowflake, Redshift, …) all POST the same payload shape
// to their integration-specific `/setup` endpoint and finalize through the shared,
// integration-agnostic `/complete-setup`. The types below are named for the flow, not
// a single warehouse, so every warehouse hook can reuse them.
type WarehouseExperimentationSetupBody = Pick<
  schemas['IntegrationConfigurationPost'],
  'name' | 'tags' | 'configValues'
>;
type WarehouseExperimentationSetupResponse = FetchResponse<Record<string | number, unknown>, unknown, MediaType>;
type PostWarehouseExperimentationSetup = (
  path: string,
  init?: { body?: unknown; headers?: HeadersInit },
) => Promise<WarehouseExperimentationSetupResponse>;

async function setupSnowflakeExperimentationIntegrationConfiguration({
  body,
}: {
  body: WarehouseExperimentationSetupBody;
}): Promise<WarehouseExperimentationSetupResponse> {
  const post = (getClient() as unknown as { POST: PostWarehouseExperimentationSetup }).POST;
  return post('/api/v2/integration-configurations/keys/snowflake-experimentation/setup', {
    body,
    headers: {
      'Content-Type': 'application/json',
      'LD-API-Version': ApiVersions.Beta,
    },
  });
}

async function setupRedshiftExperimentationIntegrationConfiguration({
  body,
}: {
  body: WarehouseExperimentationSetupBody;
}): Promise<WarehouseExperimentationSetupResponse> {
  const post = (getClient() as unknown as { POST: PostWarehouseExperimentationSetup }).POST;
  return post('/api/v2/integration-configurations/keys/redshift-experimentation/setup', {
    body,
    headers: {
      'Content-Type': 'application/json',
      'LD-API-Version': ApiVersions.Beta,
    },
  });
}

async function completeWarehouseExperimentationSetup({
  integrationConfigurationId,
  iamRoleArn,
  name,
}: {
  integrationConfigurationId: string;
  // Redshift supplies its IAM role ARN here so finalize can persist it in a single call,
  // instead of a second /setup round. Snowflake omits it and finalizes with an empty body.
  iamRoleArn?: string;
  // Optional display-name update. A draft can be renamed after generating (the name isn't
  // part of the generated scripts), and finalize is the only write that reflects it.
  name?: string;
}): Promise<WarehouseExperimentationSetupResponse> {
  const post = (getClient() as unknown as { POST: PostWarehouseExperimentationSetup }).POST;
  const body: { iamRoleArn?: string; name?: string } = {};
  if (iamRoleArn !== undefined) {
    body.iamRoleArn = iamRoleArn;
  }
  if (name !== undefined) {
    body.name = name;
  }
  return post(`/api/v2/integration-configurations/${integrationConfigurationId}/complete-setup`, {
    body,
    headers: {
      'Content-Type': 'application/json',
      'LD-API-Version': ApiVersions.Beta,
    },
  });
}

export const useSetupSnowflakeExperimentationIntegrationConfiguration = () => {
  return useMutation({
    mutationFn: async (input: { body: WarehouseExperimentationSetupBody }) =>
      (await reactQueryResponseAdapter(
        setupSnowflakeExperimentationIntegrationConfiguration(input),
      )) as IntegrationConfigurationResponse,
    onSuccess: async () => {
      const queryClient = getQueryClient();
      await queryClient.invalidateQueries({ queryKey: integrationConfigurationsQuery.partialQueryKey() });
    },
  });
};

export const useSetupRedshiftExperimentationIntegrationConfiguration = () => {
  return useMutation({
    mutationFn: async (input: { body: WarehouseExperimentationSetupBody }) =>
      (await reactQueryResponseAdapter(
        setupRedshiftExperimentationIntegrationConfiguration(input),
      )) as IntegrationConfigurationResponse,
    onSuccess: async () => {
      const queryClient = getQueryClient();
      await queryClient.invalidateQueries({ queryKey: integrationConfigurationsQuery.partialQueryKey() });
    },
  });
};

export const useCompleteWarehouseExperimentationSetup = () => {
  return useMutation({
    mutationFn: async (input: { integrationConfigurationId: string; iamRoleArn?: string; name?: string }) =>
      (await reactQueryResponseAdapter(
        completeWarehouseExperimentationSetup(input),
      )) as IntegrationConfigurationResponse,
    onSuccess: async (data, { integrationConfigurationId }) => {
      const queryClient = getQueryClient();
      // Completion returns the canonical config, including its server-assigned
      // maintainer. Seed the edit-page query directly so an immediate route
      // transition cannot observe a stale pre-completion representation.
      queryClient.setQueryData(integrationConfigurationQuery({ integrationConfigurationId }).queryKey, data);
      await queryClient.invalidateQueries({ queryKey: integrationConfigurationsQuery.partialQueryKey() });
    },
  });
};

// `/complete-setup` is keyed by configuration id and integration-agnostic: the same
// mutation finalizes any warehouse two-step setup. Snowflake predates the neutral name,
// so keep this alias to avoid churning existing Snowflake imports.
export const useCompleteSnowflakeExperimentationSetup = useCompleteWarehouseExperimentationSetup;
